'use client';

import { useState } from 'react';
import { MapPinIcon, PencilIcon, PlusIcon, XIcon } from 'lucide-react';

import { PageMain } from '@/app/(app)/page-main';
import { OfficeRegion, ServiceAreasProvider, useServiceAreasCtx } from '@/app/(app)/settings/service-areas/hooks';
import { useOfficeCtx } from '@/components/providers/v1-office-provider';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

export function ServiceAreasPage() {
  const [isCreating, setIsCreating] = useState(false);
  return (
    <ServiceAreasProvider>
      <PageMain title='Service Areas' breadcrumbs={[{ label: 'Settings', href: '/settings' }]}>
        <div className='mx-auto flex w-full max-w-2xl flex-col gap-6 rounded-lg border p-6 shadow-sm'>
          <div className='flex items-center justify-between gap-2'>
            <div className='flex flex-col gap-0.5'>
              <h1 className='text-heading-lg'>Service Areas</h1>
              <p className='text-sm text-muted-foreground'>Manage your service areas.</p>
            </div>
            <Button onClick={() => setIsCreating(true)} disabled={isCreating} size='sm'>
              <PlusIcon className='size-4' />
              Add New
            </Button>
          </div>
          <Content isCreating={isCreating} setIsCreating={setIsCreating} />
        </div>
      </PageMain>
    </ServiceAreasProvider>
  );
}

interface ContentProps {
  isCreating: boolean;
  setIsCreating: (isCreating: boolean) => void;
}

function Content({ isCreating, setIsCreating }: ContentProps) {
  const { serviceAreas } = useServiceAreasCtx();

  if (!isCreating && serviceAreas.length === 0) {
    return (
      <Card
        className='flex h-40 cursor-pointer flex-col items-center justify-center gap-3 border-dashed bg-muted/20'
        onClick={() => setIsCreating(true)}
      >
        <MapPinIcon className='size-8 text-muted-foreground' />
        <p className='text-muted-foreground'>Add your first service area</p>
      </Card>
    );
  }

  return (
    <div className='flex flex-col gap-3'>
      {isCreating && <CreateCard onCancel={() => setIsCreating(false)} onSuccess={() => setIsCreating(false)} />}
      {serviceAreas.map((area) => (
        <ListItemCard key={area.id} area={area} />
      ))}
    </div>
  );
}

function ListItemCard({ area }: { area: OfficeRegion }) {
  const [isEditing, setIsEditing] = useState(false);

  if (isEditing) {
    return <EditCard area={area} onComplete={() => setIsEditing(false)} />;
  }

  return (
    <Card className='gap-2'>
      <CardHeader>
        <div className='flex items-center justify-between gap-2'>
          <CardTitle>
            {area.name} <span className='text-muted-foreground'>({area.zip_codes?.length ?? 0})</span>
          </CardTitle>
          <Button variant='outline' size='icon' onClick={() => setIsEditing(true)}>
            <PencilIcon className='size-4' />
          </Button>
        </div>
      </CardHeader>
      {area.zip_codes?.length && area.zip_codes.length > 0 && (
        <CardContent>
          <div className='flex flex-wrap items-center gap-2'>
            {area.zip_codes?.map((zip, index) => (
              <Badge key={index}>{zip}</Badge>
            ))}
          </div>
        </CardContent>
      )}
    </Card>
  );
}

interface EditCardProps {
  area: OfficeRegion;
  onComplete: () => void;
}

function EditCard({ area, onComplete }: EditCardProps) {
  const { update, remove } = useServiceAreasCtx();
  const [name, setName] = useState(area.name);
  const [zipCodes, setZipCodes] = useState(area.zip_codes ?? []);

  const onDelete = () => {
    remove(area.id);
    onComplete();
  };

  const onSubmit = () => {
    update({ ...area, name, zip_codes: zipCodes });
    onComplete();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Edit Service Area</CardTitle>
      </CardHeader>
      <CardContent className='flex flex-col gap-4'>
        <div className='flex flex-col gap-2'>
          <Label>Name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <ZipCodeInput zipCodes={zipCodes} onChange={setZipCodes} />
        <div className='flex w-full items-center gap-2'>
          <div className='ml-auto flex gap-2'>
            <Button variant='destructive' onClick={onDelete}>
              Delete
            </Button>
            <Button variant='outline' onClick={onComplete}>
              Cancel
            </Button>
            <Button onClick={onSubmit} className='ml-auto'>
              Save
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

interface CreateCardProps {
  onCancel: () => void;
  onSuccess: () => void;
}

function CreateCard({ onCancel, onSuccess }: CreateCardProps) {
  const { officeId } = useOfficeCtx();
  const { create } = useServiceAreasCtx();
  const [name, setName] = useState('');
  const [zipCodes, setZipCodes] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = () => {
    if (name.length === 0) {
      setError('Name is required');
      return;
    }
    setError(null);
    create({ office_id: officeId, name, zip_codes: zipCodes });
    onSuccess();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Create Service Area</CardTitle>
      </CardHeader>
      <CardContent className='flex flex-col gap-4'>
        <div className='flex flex-col gap-2'>
          <Label>Name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <ZipCodeInput zipCodes={zipCodes} onChange={setZipCodes} />
        <div className='flex w-full items-center gap-2'>
          {error && <p className='text-sm font-medium text-red-500'>{error}</p>}
          <div className='ml-auto flex gap-2'>
            <Button variant='outline' onClick={onCancel}>
              Cancel
            </Button>
            <Button onClick={onSubmit} className='ml-auto'>
              Create
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

interface ZipCodeInputProps {
  zipCodes: string[];
  onChange: (zipCodes: string[]) => void;
}

function ZipCodeInput({ zipCodes, onChange }: ZipCodeInputProps) {
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState<string | null>(null);

  const validateZipCode = (code: string): boolean => {
    return /^\d{5}$/.test(code.trim());
  };

  const parseZipCodes = (input: string): string[] => {
    // Split by comma, space, newline, or tab and filter out empty strings
    return input
      .split(/[,\s\n\t]+/)
      .map((code) => code.trim())
      .filter((code) => code.length > 0);
  };

  const addZipCodes = (input: string) => {
    const newCodes = parseZipCodes(input);
    const validCodes: string[] = [];
    const invalidCodes: string[] = [];

    newCodes.forEach((code) => {
      if (validateZipCode(code)) {
        validCodes.push(code);
      } else {
        invalidCodes.push(code);
      }
    });

    if (invalidCodes.length > 0) {
      setError(`Invalid zip codes: ${invalidCodes.join(', ')}. Zip codes must be 5 digits.`);
      return;
    }

    // Deduplicate by combining existing and new codes, then removing duplicates
    const allCodes = [...zipCodes, ...validCodes];
    const uniqueCodes = Array.from(new Set(allCodes));

    setError(null);
    onChange(uniqueCodes);
    setInputValue('');
  };

  const removeZipCode = (index: number) => {
    const newZipCodes = zipCodes.filter((_, i) => i !== index);
    onChange(newZipCodes);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      e.preventDefault();
      addZipCodes(inputValue);
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    if (pastedText) {
      addZipCodes(pastedText);
    }
  };

  return (
    <div className='flex flex-col gap-2'>
      <Label>Zip Codes</Label>
      <div className='flex flex-row items-start gap-2'>
        <Textarea
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          placeholder='Enter zip codes (comma, space, or newline separated).'
          className='min-h-9 resize-y'
        />
        <Button onClick={() => addZipCodes(inputValue)} disabled={!inputValue.trim()} className='w-24'>
          Add
        </Button>
      </div>

      {error && <p className='text-sm font-medium text-red-500'>{error}</p>}

      {zipCodes.length > 0 && (
        <div className='flex flex-wrap items-center gap-2'>
          {zipCodes.map((code, index) => (
            <Badge key={index} variant='secondary'>
              {code}
              <button onClick={() => removeZipCode(index)} className='ml-2 text-muted-foreground hover:text-foreground'>
                <XIcon className='size-3' />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}
